/**
 * Money, formatted once. Ported from `asthra-v4-web/src/money.ts` so the add-in
 * and the web app agree on what `$1,999` and a foreign-currency receipt look
 * like. Pure arithmetic about a number — nothing here knows what a tier is.
 */

/**
 * Minor units (USD cents) to a display string. Cents shown only when non-zero.
 */
export function formatUsd(minor: number): string {
  const whole = Math.trunc(minor / 100);
  const cents = Math.abs(minor % 100);
  const dollars = whole.toLocaleString("en-US");
  return cents === 0 ? `$${dollars}` : `$${dollars}.${String(cents).padStart(2, "0")}`;
}

/**
 * Minor units in an arbitrary currency, for a receipt. What we CHARGE is not
 * always USD (the provider is merchant of record and prices adaptively), and
 * minor units are not always hundredths (yen/won have no subunit), so the
 * exponent and symbol come from `Intl`, not a hand-rolled `/100`.
 */
export function formatMoney(minor: number, currency: string): string {
  const code = currency.trim().toUpperCase();
  try {
    const format = new Intl.NumberFormat("en-US", { style: "currency", currency: code });
    const digits = format.resolvedOptions().maximumFractionDigits ?? 2;
    const major = minor / 10 ** digits;
    return Number.isInteger(major)
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: code,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(major)
      : format.format(major);
  } catch {
    return `${(minor / 100).toFixed(2)} ${code}`.trimEnd();
  }
}
